import { BLANK_CREATIVE_IDS } from "../constants";
import { AdAttribute } from "../../../globals";
import { getCookie, setCookie } from "../../../../common/cookie-util";
import { ZEUS_SCRIPT_URL } from "../hooks/useAdsScripts";
import { isWapoDomain } from "../../utils";
import { isAdFree } from "../../../../common/isAdFree";

export const isAdAllowed = (video, config = {}) => {
  const adSetUrlApp =
    video?.additional_properties?.advertising?.adSetUrls?.apps ?? "";
  const adZone = video?.additional_properties?.advertising?.videoAdZone ?? "";
  const isValidAdZone = /wpni.video/.test(adZone) === true;

  const { adOverrides = {}, debug } = config;
  const { domain } = adOverrides;

  return (
    (!!adOverrides.adZoneOverride || (!!adSetUrlApp && !!adZone)) &&
    isValidAdZone &&
    isAllowedOnDomain(video, domain, debug)
  );
};

export const isBlankAd = (adData = {}) => {
  if (adData && typeof adData.creativeId === "string") {
    return BLANK_CREATIVE_IDS.includes(adData.creativeId);
  }
  return null;
};

export const getAvAdMeta = (adData = {}, skipTime = -1) => {
  return [
    `length:${adData.duration}`,
    `skipAllow:${skipTime > 0}`,
    `skipDelay:${skipTime}`
  ];
};

export const getAdAttribute = adData => {
  return isBlankAd(adData) ? AdAttribute.BLANK : AdAttribute.STARTED;
};

export const getAdCompleteAttribute = adData => {
  return isBlankAd(adData) ? AdAttribute.BLANK : AdAttribute.COMPLETE;
};

export const getSkipData = (remainingTime, currentTime, skipTimeOffset) => {
  const skipReady = skipTimeOffset > 0 && currentTime > skipTimeOffset;
  const skipCountDown = skipTimeOffset - currentTime;
  return {
    skipReady: skipReady,
    skipCountDown: skipCountDown,
    remainingTime: remainingTime
  };
};

export const getAdData = irisPlayer => {
  return irisPlayer.getCurrentAd()?.data;
};

export function isAllowedOnDomain(videoData, domainOverride, debug) {
  if (isWapoDomain(domainOverride)) {
    return true;
  }

  const allowed =
    videoData?.additional_properties?.advertising?.allowPrerollOnDomain;

  if (debug && !allowed) console.log("Ad not allowed on domain");
  return allowed;
}

/**
 * @description Check if ads should play for a particular video. Even if this returns false, ads may play for subsequent vids
 * in playthrough/playlist
 * @param {Object} video - the ANS object for the video that is about to play
 * @param {Object} config - the orca component's config prop. If ads are turned off in the config, they will never render.
 * @returns {boolean}
 */
export function videoHasAds(video, config) {
  const {
    hasAds = false,
    adOverrides: { forceAd = false, domain, skipAd = false } = {},
    isCarousel = false,
    debug
  } = config;
  // forceAd overrides other config/ANS settings
  if (forceAd) return true;
  const {
    additional_properties: {
      advertising: { playVideoAds = true } = {
        playVideoAds: true
      }
    } = {
      advertising: {}
    }
  } = video;

  return (
    hasAds &&
    // only play ads if ANS says to, or if it's a carousel (carousel videos can have ads even if ANS says no)
    (playVideoAds || isCarousel) &&
    isAllowedOnDomain(video, domain, debug) &&
    !skipAd
  );
}

export function autoplayStateAllowsAds(autoplayState = {}) {
  // in general autoplay videos should not play ads. However, we make exceptions for non subs and playthrough
  return (
    !autoplayState.isAutoplay ||
    !!autoplayState.isPreroll ||
    !!autoplayState.isPlaythrough
  );
}

export const setNoAdsCookie = videoId => {
  if (typeof document === "undefined") return;
  const coookieStr = getCookie("wp_no_preroll");
  const cookieObject = coookieStr ? JSON.parse(coookieStr) : {};
  cookieObject[videoId] = "1";
  const updatedCookieStr = JSON.stringify(cookieObject);
  setCookie("wp_no_preroll", updatedCookieStr, 1);
};

const hasNoAdCookie = videoId => {
  if (!getCookie("wp_no_preroll")) return false;
  const cookieObject = JSON.parse(getCookie("wp_no_preroll"));
  return cookieObject[videoId] === "1";
};

/**
 * @description Check if ads are turned off in the config prop. If that is the case, ads will never play in this orca element
 * @param {Object} config - the player's config prop
 * @returns {boolean}
 */
export function getShouldShowAds(config, videoId) {
  const {
    hasAds = false,
    adOverrides: { forceAd = false } = {},
    isSinglePrerollVideo
  } = config;
  // forceAd overrides other config/ANS settings
  if (forceAd) return true;
  if (isSinglePrerollVideo && hasNoAdCookie(videoId)) return false;
  return hasAds && !getNoAdsParam() && !isAdFree();
}

export const getNoAdsParam = () => {
  if (typeof window === "undefined") return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has("no_ads");
};

// get initial isMuted value for an adTag URL param
export const getInitMuteValue = () => {
  if (typeof window !== "undefined") {
    const mutePreference = localStorage.getItem("userMutePreference");
    if (mutePreference === "true") {
      return true;
    } else if (mutePreference === "false") {
      return false;
    }
  }
  return false;
};

const ZEUS_STAGING_URL = "https://www.washingtonpost.com/zeus-staging/main.js";

export const getZeusScriptUrl = () => {
  if (typeof window === "undefined") return false;
  const urlParams = new URLSearchParams(window.location.search);
  const zeusEnv = urlParams.get("zeusEnv");
  return zeusEnv === "staging" ? ZEUS_STAGING_URL : ZEUS_SCRIPT_URL;
};
